import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import SideNavItem from './item/item';
import { Icon, SiteIcon } from 'assets/icons';
import { nav } from './config.js';
import { addDataToStore } from 'store/actions/user-data';
import { useTenantService } from 'hooks/api/iam/tenant';
import { truncateText } from 'app/shared/utils/general';
import useScreenSize from 'hooks/size';
import profile_icon from 'assets/images/profile_icon.png';

import styles from './side-nav.module.css';

const SideNav = (props) => {
  const { open_tray, onTrayClose } = props;
  const dispatch = useDispatch();
  const history = useNavigate();
  const veil = useRef();
  const { profile_image, tenant_id } = useSelector((state) => state.user_data);
  const { isDesktop, isMiniDesktop } = useScreenSize();
  const { fetchTenant } = useTenantService();

  const [show_action_menu, setShowActionMenu] = useState(false);
  const [tenant, setTenant] = useState({});

  const menu_items = [
    [
      {
        icon_name: 'settings',
        label: 'Settings',
        onClick: () => {
          history('/settings');
          closeMenuTray();
        }
      }
    ],
    [
      {
        color: 'var(--red-primary)',
        icon_name: 'logout',
        label: <span style={{ fontWeight: 500 }}>Log out</span>,
        onClick: () => {
          dispatch(addDataToStore({}));
        }
      }
    ]
  ];

  useEffect(() => {
    if (!tenant_id) return;
    fetchTenant(tenant_id).then(({ tenant }) => {
      if (tenant) setTenant(tenant);
    });
  }, [tenant_id]);

  useEffect(() => {
    if (open_tray) {
      openMenuTray();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open_tray]);

  const closeMenuTray = () => {
    const tray = veil.current;
    let tray_left = 0;
    fadeOutTrayBackground();
    const slideOutAmination = setInterval(() => {
      if (tray_left > 100) {
        tray.style.left = '100%';
        clearInterval(slideOutAmination);
        onTrayClose();
        return;
      }

      tray_left += 10;
      tray.style.left = `-${tray_left}%`;
    }, 20);
  };

  const fadeInTrayBackground = () => {
    const tray = veil.current;
    let opacity = 0;
    const fadeInAnimation = setInterval(() => {
      if (opacity > 0.3) {
        tray.style.background = 'rgba(0, 0, 0, 0.35)';
        clearInterval(fadeInAnimation);
        return;
      }

      opacity += 0.1;
      tray.style.background = `rgba(0, 0, 0, ${opacity})`;
    }, 10);
  };

  const fadeOutTrayBackground = () => {
    const tray = veil.current;
    let opacity = 0.35;
    const fadeOutAnimation = setInterval(() => {
      if (opacity < 0) {
        tray.style.background = 'rgba(0, 0, 0, 0)';
        clearInterval(fadeOutAnimation);
        return;
      }

      opacity -= 0.1;
      tray.style.background = `rgba(0, 0, 0, ${opacity})`;
    }, 10);
  };

  const openMenuTray = () => {
    const tray = veil.current;
    let tray_left = 100;
    tray.style.background = 'rgba(0, 0, 0, 0)';
    const slideInAmination = setInterval(() => {
      if (tray_left < 0) {
        tray.style.left = 0;
        fadeInTrayBackground();
        clearInterval(slideInAmination);
        return;
      }

      tray_left -= 10;
      tray.style.left = `-${tray_left}%`;
    }, 20);
  };

  const renderItem = (item, id) => {
    if (!item) return null;
    return (
      <div
        key={`action-menu-item-${id}`}
        className={styles.actionMenuItem}
        onClick={(e) => {
          item.onClick(e);
          setShowActionMenu(false);
        }}
      >
        <Icon
          width={16}
          height={16}
          className={styles.actionMenuItemIcon}
          name={item.icon_name}
          color={item.color}
        />
        <div className={styles.actionMenuItemLabel} style={{ color: item.color }}>
          {item.label}
        </div>
      </div>
    );
  };

  return (
    <nav className={styles.sideNavWrapper} ref={veil} onClick={() => closeMenuTray()}>
      <div className={styles.appSideNav} onClick={(e) => e.stopPropagation()}>
        <div>
          <header>
            <Link to="/dashboard" className={styles.link}>
              <span className={styles.siteIcon}>
                <SiteIcon width={24} height={24} />
              </span>
            </Link>
          </header>
          <section>
            {nav.map((nav_section) => {
              return nav_section.hide_if &&
                nav_section.hide_if({ isDesktop, isMiniDesktop }) ? null : (
                <div className={styles.navGroup} key={`nav-section-${nav_section.title}`}>
                  {nav_section.has_horizontal_rule && <hr />}
                  <div>
                    {nav_section.items.map((item) =>
                      item.hide_if && item.hide_if({ isDesktop, isMiniDesktop }) ? null : (
                        <SideNavItem
                          key={`nav-item-${item.title}`}
                          {...item}
                          onItemClick={closeMenuTray}
                        />
                      )
                    )}
                  </div>
                </div>
              );
            })}
          </section>
        </div>

        {show_action_menu && (
          <div className={styles.actionMenu}>
            {menu_items.map((group, id) => (
              <React.Fragment key={`menu-group-${id}`}>
                {id > 0 && <hr />}
                <div className={styles.actionMenuGroup}>
                  {group.map((item, idx) => renderItem(item, idx))}
                </div>
              </React.Fragment>
            ))}
          </div>
        )}

        <div className={styles.footer} onClick={() => setShowActionMenu((show) => !show)}>
          <div>
            <div className={styles.icon}>
              <img src={profile_image || profile_icon} alt="Profile Icon" />
            </div>
            <div className={styles.orgDetails}>
              <div className={styles.orgTitle}>{truncateText(tenant?.name, 15)}</div>
              <div className={styles.orgTenantId}>Tenant ID: {tenant?.id}</div>
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default SideNav;
